import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    Channel,
    Envs,
    EventType,
    useAppContext,
    useChannelContext,
    useCurrentPage,
    useEnvContext,
    useEventBusEmit,
    useFindMenuTitle,
    useLocale,
    useMenuAuthority,
    useQuery,
    useThrottle,
} from 'scbk-services';
import { useAppBridge, useWebviewBridge } from 'scbk-services/bridge';
import { useRouteDataStorage, useRouter, useRouterRegistry } from 'scbk-services/router';
import { NavigateOptions } from 'scbk-services/types/route/route';
import { WebviewVersion } from 'scbk-services/utils/hooks/useWebviewStack';
import { useRestoreDataStore } from '../../page/stores/restoreDataStore';
import { useMenuContextStore } from '../stores/useMenuContextStore';
import { useMenuStore } from '../stores/useMenuStore';

export interface WebviewOptions {
    closeCurrentWebview?: boolean;
}

export interface MenuOptions extends NavigateOptions {
    forceCheckCode?: string;
    webviewOptions?: WebviewOptions;
    clearStateData?: boolean;
    skipMenuAuthority?: boolean;
}

/**
 *
 * @param prefix
 * @returns
 */
function createWebviewId(prefix = 'v4'): string {
    return `webview-${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 *
 * @param reqUrl
 * @returns
 */
const parseRequestUrl = (
    reqUrl?: string | string[],
): { uri: string; params: string | undefined } => {
    const value = Array.isArray(reqUrl) ? reqUrl[0] : (reqUrl ?? '');
    const index = value.indexOf('?');

    if (index < 0) {
        return {
            uri: value,
            params: '',
        };
    }

    return {
        uri: value.substring(0, index),
        params: value.substring(index + 1) ?? '',
    };
};

let submitting = false;

/**
 * 메뉴이동 유틸리티 hook
 * @returns
 */
function useMenuRouter() {
    const { mergeQuery, appendQueryString, getQuery, queryToObject, toQueryString } = useQuery();
    //쿼리스트링
    const router = useRouter();
    const find = useMenuStore((state) => state.find);
    const findByPath = useMenuStore((state) => state.findByPath);
    const setCurrentMenuItem = useMenuStore((state) => state.setCurrentMenuItem); // 현재 menu set
    const currentMenu = useMenuStore((state) => state.currentMenu);
    const { findModuleRouteObjectsByPath } = useRouterRegistry();
    const { channel } = useChannelContext();
    const webviewBridge = useWebviewBridge(); // native 멀티 웹뷰 처리
    const { doNGetWebviewStorage, doNRemoveWebviewStorage } = useAppBridge();
    const { set: saveMenuRouteData } = useRouteDataStorage();
    //const { checkRoute } = useAccessCheckRoute();
    const emit = useEventBusEmit();

    const { clearAll: clearRestoreData } = useRestoreDataStore();
    const { clearMenuContext } = useMenuContextStore();

    // document title
    const { findMenuTitle } = useFindMenuTitle();

    //메뉴 권한체크
    const { checkMenuInfo } = useMenuAuthority();

    const { getContext } = useAppContext();

    const { getLocale } = useLocale();

    const { currentPage } = useCurrentPage();

    const { getEnv } = useEnvContext();

    /**
     * 업무 웹뷰 Open
     * @param type 웹뷰타입
     * @param url url 문자열
     * @param query queryString 문자열 또는 URLSearchParams
     * @param webviewOptions 웹뷰 Option
     */
    const callWebview = useCallback(
        async (
            webviewType: WebviewVersion = 'v4',
            url: string,
            query?: string | URLSearchParams,
            routeData?: Record<string, string | undefined | null>,
            webviewOptions?: WebviewOptions,
        ) => {
            const [base, search = ''] = url.split('?');

            const resolvedQuery = mergeQuery(search, query);

            const webviewUrl = `${base}?${resolvedQuery.toString()}`;
            let postData: Record<string, unknown> = {};

            //********************************************************************
            //웹뷰ID/웹뷰 버전 쿼리스트링 추가
            //********************************************************************
            const currentWebviewType = getQuery('webviewType') ?? 'v4';
            const webviewQueryString = `webviewId=${createWebviewId(webviewType)}&org_webview_type=${currentWebviewType}&ma3.lang.code=${getLocale() ?? 'ko'}`;
            let resolvedWebviewUrl = appendQueryString(webviewUrl, webviewQueryString);

            //********************************************************************
            //웹뷰타입이 v3인 경우 ma30 기본 요청데이터를 설정한다.
            //UserIP,IPINSIDE_AX,IPINSIDE_MAC,IPINSIDE_HDD,DevicePhone,DEVICETOKEN,ma3.lang.code
            //********************************************************************
            if (webviewType === 'v3') {
                //********************************************************************
                //중복파라미터 제거
                //********************************************************************
                const params = new URLSearchParams(resolvedQuery);
                params.forEach((_, key) => {
                    delete routeData?.[key];
                });

                const { ipinside, device, darkMode, seniorMode } = getContext();
                const userIp = ipinside?.natip ?? ''; //IPINSIDE_IP
                const mac = ipinside?.mac ?? ''; //IPINSIDE_MAC
                const mduinfo = ipinside?.mduinfo ?? ''; //IPINSIDE_HDD
                const wdata = ipinside?.wdata ?? ''; //IPINSIDE_AX
                const deviceToken = device?.deviceToken ?? '';
                const devicePhone = '00000000000'; //DevicePhone
                const locale = getLocale() ?? 'ko'; //ma3.lang.code
                const prevPage = currentPage ? currentPage.path : ''; //asis prev.page

                postData = {
                    ...routeData,
                    UserIP: userIp,
                    IPINSIDE_AX: wdata,
                    IPINSIDE_MAC: mac,
                    IPINSIDE_HDD: mduinfo,
                    DevicePhone: devicePhone,
                    DEVICETOKEN: deviceToken,
                    'ma3.lang.code': locale,
                    darkMode,
                    seniorMode,
                    'prev.page': prevPage,
                };

                // if (webviewQueryString.includes('changeViewService')) {
                //     const { changeViewService, ...filteredPostData } = postData;
                //     postData = { ...filteredPostData };
                // }

                //TODO 안드로이드인 경우 일단 임시로 쿼리스트링에 포함
                //if (deviceUtils.isAndroid()) {
                //const v3QueryString = toQueryString(postData);
                //resolvedWebviewUrl = appendQueryString(resolvedWebviewUrl, v3QueryString);
                //}

                console.log(
                    `# useMenuRouter callWebview webviewType=${webviewType}, postData=${postData}`,
                );
            } else {
                //react 는
                const queryString = toQueryString({ ...routeData });
                resolvedWebviewUrl = appendQueryString(resolvedWebviewUrl, queryString);
            }

            console.log(`### webviewType=${webviewType}, resolvedQuery=${resolvedQuery}`);

            //********************************************************************
            //현재웹뷰 비활성 상태로 업데이트
            //********************************************************************
            //webviewContext.inactiveWebview();
            //웹뷰 오픈 이벤트를 발생시킨다.
            emit(EventType.WebviewOpen);

            //********************************************************************
            // push 후 return 된 url을 이용하여 브릿지 호출한다.
            //********************************************************************
            await webviewBridge.doNOpenBizWebView({
                type: webviewType,
                url: resolvedWebviewUrl,
                closeCurrentWebview: webviewOptions?.closeCurrentWebview ?? false,
                method: webviewType === 'v3' ? 'POST' : 'GET', //v3인 경우 POST 방식으로 open 하도록 한다.
                data: webviewType === 'v3' ? JSON.stringify(postData) : null,
            });
        },
        [webviewBridge],
    );

    /**
     * routeObject 정보 조회
     */
    const doRoute = useCallback(
        async (url: string, options?: MenuOptions, isReplace: boolean = false) => {
            const routeObject = findModuleRouteObjectsByPath(url);
            if (_.isEmpty(routeObject)) {
                if (getEnv() === Envs.LOCAL || getEnv() === Envs.DEV) {
                    alert('[DEV] 등록된 route 정보가 없습니다.\r\n[' + url + ']');
                }
                return;
            }

            let query = options?.query;
            const queryIndex = url.indexOf('?');
            if (queryIndex > -1) {
                query = mergeQuery(url.slice(queryIndex), query);
            }

            const { id = '' } = routeObject;
            const opt: NavigateOptions = {
                ...options,
                query,
                routeType: 'menu',
            };
            isReplace ? router.replace(id, { ...opt }) : router.push(id, { ...opt });
        },
        [findModuleRouteObjectsByPath, getEnv],
    );

    /**
     * 메뉴이동
     * @param menuId 메뉴ID
     * @param options 메뉴이동 옵션
     * @param isReplace replace 여부
     */
    const goMenu = useCallback(
        async (menuId: string, options?: MenuOptions, isReplace: boolean = false) => {
            try {
                if (submitting) {
                    return;
                }

                const menu = find(menuId);
                if (!menu) {
                    if (getEnv() === Envs.LOCAL || getEnv() === Envs.DEV) {
                        console.error('[DEV] 메뉴정보가 없습니다.', menuId);
                        alert('[DEV] 메뉴정보가 없습니다.');
                    }
                    return;
                }

                const query = new URLSearchParams(options?.query);
                query.set('menuId', menuId);

                const _opt: MenuOptions = {
                    ...options,
                    query,
                };

                const url: string = menu.url ?? '';

                if (!url) {
                    if (getEnv() === Envs.LOCAL || getEnv() === Envs.DEV) {
                        console.error('[DEV] 메뉴에 설정된 URL 문자열이 없습니다.', menuId);
                        alert('[DEV] 메뉴에 설정된 URL 문자열이 없습니다.');
                    }
                    return;
                }

                const forceCheckCode = options?.forceCheckCode ?? '';
                const skipMenuAuthority = options?.skipMenuAuthority ?? false;

                //********************************************************************
                //메뉴 체크
                //TOOD 세션클리어
                //********************************************************************
                if (!skipMenuAuthority) {
                    const checkedMenu = await checkMenuInfo(menu, forceCheckCode); // 세션클리어
                    if (!checkedMenu) {
                        return;
                    }
                }

                //********************************************************************
                //이전에 저장된 restore data 정보 제거
                //[2026.07.22 최성돈] goMenu 이후 이전 화면으로 이동 시 이전 화면 데이터가 남아야 하는 이슈 발생
                //clearStateData 옵션으로 처리한다.
                //********************************************************************
                const clearStateData = options?.clearStateData ?? true;
                if (clearStateData) {
                    clearRestoreData();
                }
                clearMenuContext();

                //모바일뱅킹인경우 처리
                if (channel === Channel.MB) {
                    //로컬개발환경인경우 push route 처리
                    if (getEnv() === Envs.LOCAL) {
                        //********************************************************************
                        //내부 화면이동시에만 타이틀 변경
                        //********************************************************************
                        document.title = findMenuTitle(menuId) ?? 'SC Bank';
                        doRoute(url, _opt, isReplace);
                        return;
                    }

                    //options으로 넘어온 webviewType이 있다면.. options 의 webviewType을 우선으로 본다.
                    const overrideWebviewType = options?.webviewType;
                    const menuWebviewType = menu?.fields?.webviewType ?? '';
                    const resolvedWebviewType = overrideWebviewType ?? menuWebviewType;

                    if (!resolvedWebviewType || '' === resolvedWebviewType) {
                        console.warn('# 메뉴에 설정된 webviewType 필드가 없습니다.');
                        return;
                    }

                    //********************************************************************
                    //현재 웹뷰타입과 호출대상 웹뷰타입이 같으면.. 현재 웹뷰에서 router.push를 진행한다.
                    //********************************************************************
                    const currentWebviewType = getQuery('webviewType') ?? '';
                    if (currentWebviewType === resolvedWebviewType) {
                        //********************************************************************
                        //메뉴정보 저장
                        //********************************************************************
                        setCurrentMenuItem(menu);

                        //********************************************************************
                        //내부 화면이동시에만 타이틀 변경
                        //********************************************************************
                        document.title = findMenuTitle(menuId) ?? 'SC Bank';

                        query.set('webviewType', resolvedWebviewType);
                        doRoute(
                            url,
                            {
                                ..._opt,
                                query,
                            },
                            isReplace,
                        );
                    } else {
                        //********************************************************************
                        //메뉴 routeData 로컬 스토리지에 저장
                        //********************************************************************
                        let query = _opt?.query ?? '';

                        if (_opt.routeData) {
                            saveMenuRouteData(menuId, _opt.routeData);

                            /*if (resolvedWebviewType === 'v3') {
                                query = mergeQuery(query, toQueryString(_opt.routeData ?? {}));
                            }*/
                        }

                        //메뉴식별자 쿼리스트링 추가
                        //const resolvedUrl = appendQuery(url, 'menuId', menuId);

                        // 2026.09.22 storage 저장 데이터도 정리
                        if (sessionStorage) {
                            const BIZ_DATA = JSON.parse(sessionStorage.getItem('BIZ_DATA') || '{}');
                            if (BIZ_DATA['BIZ_DATA_CLEAR'] !== 'N') {
                                sessionStorage.removeItem('BIZ_DATA');
                            }
                        }

                        const bizData = JSON.parse(
                            (await doNGetWebviewStorage('BIZ_DATA')) || '{}',
                        );
                        if (channel === Channel.MB && bizData['BIZ_DATA_CLEAR'] !== 'N') {
                            await doNRemoveWebviewStorage('BIZ_DATA');
                        }

                        await callWebview(
                            resolvedWebviewType as WebviewVersion,
                            url,
                            query,
                            _opt.routeData,
                            options?.webviewOptions ?? ({} as WebviewOptions),
                        );
                    }
                    //인터넷뱅킹인경우 처리
                } else {
                    //********************************************************************
                    //메뉴정보 저장
                    //********************************************************************
                    setCurrentMenuItem(menu);

                    //********************************************************************
                    //내부 화면이동시에만 타이틀 변경
                    //********************************************************************
                    document.title = findMenuTitle(menuId) ?? 'SC Bank';

                    await doRoute(
                        url,
                        {
                            ..._opt,
                            query,
                        },
                        isReplace,
                    );
                }
            } finally {
                submitting = false;
            }
        },
        [router, find, setCurrentMenuItem, currentMenu, saveMenuRouteData, getEnv],
    );

    /**
     * 메뉴 이동
     * @param menuId 메뉴 식별자
     * @param options 메뉴이동시 설정 Option
     */
    const goto = useThrottle(
        useCallback(
            async (menuId: string, options?: MenuOptions) => {
                await goMenu(menuId, options);
            },
            [goMenu],
        ),
        1000,
    );

    /**
     * 메뉴 이동(replace)
     * @param menuId 메뉴 식별자
     * @param options 메뉴이동시 설정 Option
     */
    const replaceTo = useThrottle(
        useCallback(
            async (menuId: string, options?: MenuOptions) => {
                await goMenu(menuId, options, true);
            },
            [goMenu],
        ),
        1000,
    );

    /**
     * PMS 내알림 WEB URL 대응 메뉴이동처리 함수
     * @param url 이동메뉴 URL
     */
    const goUrl = useCallback(
        async (url: string) => {
            if (_.isEmpty(url)) {
                return;
            }

            let resolvedUrl = url;

            const arr = url.split(':');
            if (arr.length > 1) {
                resolvedUrl = arr[arr.length - 1] as string;
            }

            const { uri, params = '' } = parseRequestUrl(resolvedUrl);
            const find = findByPath(uri);
            if (!find) {
                console.log(`# goUrl 등록되지 않는 메뉴정보 uri=${uri}`);
                return;
            }

            const { id, fields = {} } = find;

            //************************************
            // 결함 157140 대응 메인 홈 요청인경우 처리
            //************************************
            const navbarType = fields['navbarType'] ?? '';
            const webviewType = fields['webviewType'] ?? '';
            if (_.isNotEmpty(navbarType) && 'main' === webviewType) {
                window.dispatchEvent(
                    new CustomEvent('webview:goMain', {
                        detail: {
                            id,
                            url: `${uri}?webviewType=${webviewType}`,
                        },
                    }),
                );
                return;
            }

            const data = queryToObject(params);
            //const changeViewService = data['changeViewService'];

            goMenu(id, {
                query: params,
                routeData: { ...data },
            });
        },
        [findByPath],
    );

    return {
        goMenu: goto,
        replaceMenu: replaceTo,
        goUrl,
    };
}

/**
 * 현재 메뉴정보를 가져오는 hook
 * @returns
 */
function useCurrentMenu() {
    const currentMenu = useMenuStore((state) => state.currentMenu);
    const currentMenuRef = useRef(currentMenu);

    useEffect(() => {
        currentMenuRef.current = currentMenu;
    }, [currentMenu]);

    /**
     * 현재 메뉴정보를 가져온다.
     */
    const currentMenuInfo = useMemo(() => {
        return currentMenuRef.current;
    }, [currentMenuRef.current]);

    /**
     * 현재 메뉴 필드정보를 가져온다.
     * @param fieldName 메뉴필드명
     * @returns 메뉴필드명으로 조회한 결과값
     */
    const getMenuField = useCallback(
        (fieldName: string): string => {
            if (!currentMenuRef.current) {
                return '';
            }

            if (!currentMenuRef.current.fields) {
                return '';
            }
            return currentMenuRef.current.fields[fieldName];
        },
        [currentMenuRef.current],
    );

    /**
     * 현재 메뉴 파라미터정보를 가져온다.
     * @param parameterName 파라미터명
     * @returns 메뉴파라미터명으로 조회한 결과값
     */
    const getMenuParameter = useCallback(
        (parameterName: string): string => {
            if (!currentMenuRef.current) {
                return '';
            }

            if (!currentMenuRef.current.parameter) {
                return '';
            }
            return currentMenuRef.current.parameter[parameterName];
        },
        [currentMenu],
    );

    return {
        currentMenuId: currentMenuInfo?.id,
        currentMenuInfo,
        getMenuField,
        getMenuParameter,
    };
}

export { useCurrentMenu, useMenuRouter };
